import React, { useState, useEffect } from 'react';
import {
    Box,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TablePagination,
    TableRow,
    TextField,
    Button,
    CircularProgress,
    InputAdornment,
    Typography,
    Dialog,
    DialogTitle,
    DialogContent,
    List,
    Avatar,
    Stack,
    IconButton
} from '@mui/material';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import StarIcon from "@mui/icons-material/Star";
import StarHalfIcon from "@mui/icons-material/StarHalf";
import StarBorderIcon from "@mui/icons-material/StarBorder";
import SearchIcon from '@mui/icons-material/Search';
import { reportService} from '../../../services/api';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import CloseIcon from '@mui/icons-material/Close';

const SearchInsightsReport = () => {
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [data, setData] = useState([]);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(false);
    const [filters, setFilters] = useState({ search: '' });
    const [searchTimeout, setSearchTimeout] = useState(null);
    const [avgRatingMap, setAvgRatingMap] = useState({});
    const [ratingDialog, setRatingDialog] = useState({
        open: false,
        contentId: null,
        contentName: '',
        data: [],
        loading: false
    });

    const columns = [
        { id: 'query', label: 'Searched Term', minWidth: 170 },
        { id: 'matchPercentage', label: '% Matched to Modules', minWidth: 100 },
        { id: 'mostClickedModule', label: 'Most Clicked Module', minWidth: 200 },
        { id: 'avgRating', label: 'Avg. Rating', minWidth: 120 }
    ];

    const fetchData = async () => {
        try {
            setLoading(true);
            const response = await reportService.getSearchInsights({
                page,
                limit: rowsPerPage,
                search: filters.search
            });

            if (response.data.success) {
                const rows = response.data.data;
                setData(response.data.data);
                setTotal(response.data.total);
            

                // load average ratings for displayed rows
                const contentIds = Array.from(new Set(rows.map(r => r.mostClickedModuleId).filter(Boolean)));
                if (contentIds.length) {
                    const promises = contentIds.map(id =>
                        reportService.getContentRating(id)
                            .then(res => ({ id, data: res.data.success ? res.data.data : { avgRating: 0, totalRatings: 0 } }))
                            .catch(() => ({ id, data: { avgRating: 0, totalRatings: 0 } }))
                    );
                    const results = await Promise.all(promises);
                    const map = {};
                    results.forEach(r => {
                        map[r.id] = {
                            avgRating: Number(r.data.avgRating || 0),
                            totalRatings: Number(r.data.totalRatings || 0)
                        };
                    });
                    setAvgRatingMap(map);
                } else {
                    setAvgRatingMap({});
                }
            }
        } catch (error) {
            console.error('Error fetching search insights:', error);
        } finally {
            setLoading(false);
        }
    };

    // fetch average ratings for displayed rows (optional): we'll fetch on demand for modal
    const openRatings = async (contentId, contentName) => {
        try {
            setRatingDialog(prev => ({ ...prev, open: true, contentId, contentName, loading: true, data: [] }));
            const resp = await reportService.getContentRatings(contentId, 0, 100);
            if (resp.data.success) {
                setRatingDialog(prev => ({ ...prev, data: resp.data.data || [], loading: false }));
            } else {
                setRatingDialog(prev => ({ ...prev, loading: false }));
            }
        } catch (error) {
            console.error('Error fetching content ratings:', error);
            setRatingDialog(prev => ({ ...prev, loading: false }));
        }
    };

    // helper to fetch avg for a single row (used if you want live avg column)
    const fetchAvgForRow = async (contentId) => {
        try {
            const resp = await reportService.getContentRating(contentId);
            if (resp.data.success) return resp.data.data;
        } catch (err) {
            return { avgRating: 0, totalRatings: 0 };
        }
        return { avgRating: 0, totalRatings: 0 };
    };

    useEffect(() => {
        fetchData();
    }, [page, rowsPerPage]);

    useEffect(() => {
        if (searchTimeout) {
            clearTimeout(searchTimeout);
        }
        setSearchTimeout(
            setTimeout(() => {
                setPage(0);
                fetchData();
            }, 500)
        );
    }, [filters.search]);

    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(+event.target.value);
        setPage(0);
    };

    const handleExport = async () => {
        try {
            setLoading(true);

            // fetch full report rows (get everything for export)
            const resp = await reportService.getSearchInsights({
                page: 0,
                limit: 10000,
                search: filters.search
            });

            if (!resp.data.success) {
                throw new Error('Failed to fetch report data for export');
            }

            const rows = resp.data.data || [];

            // collect unique content ids to fetch avg ratings in batch
            const contentIds = Array.from(new Set(rows.map(r => r.mostClickedModuleId).filter(Boolean)));

            const ratingMap = {};
            if (contentIds.length) {
                const ratingPromises = contentIds.map(id =>
                    reportService.getContentRating(id)
                        .then(r => r.data && r.data.success ? r.data.data : { avgRating: 0, totalRatings: 0 })
                        .catch(() => ({ avgRating: 0, totalRatings: 0 }))
                );
                const ratingResults = await Promise.all(ratingPromises);
                contentIds.forEach((id, idx) => {
                    ratingMap[id] = ratingResults[idx] || { avgRating: 0, totalRatings: 0 };
                });
            }

            // build CSV with new Avg. Rating column (format: "4.3/5 (10)")
            const headers = ['Search Term', 'Total Searches', 'Match %', 'Most Clicked Module', 'Avg. Rating'];
            let csv = headers.join(',') + '\n';

            rows.forEach(row => {
                const avg = row.mostClickedModuleId ? ratingMap[row.mostClickedModuleId] : { avgRating: 0, totalRatings: 0 };
                const avgStr = `${avg.avgRating ? Number(avg.avgRating).toFixed(1) : '0.0'}/5 (${avg.totalRatings || 0})`;

                const line = [
                    `"${(row.query || '').replace(/"/g, '""')}"`,
                    row.totalSearches || 0,
                    row.matchPercentage || 0,
                    `"${(row.mostClickedModule || '').replace(/"/g, '""')}"`,
                    `"${avgStr}"`
                ].join(',');

                csv += line + '\n';
            });

            const blob = new Blob([csv], { type: 'text/csv' });
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `search-insights-${new Date().toISOString()}.csv`;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            window.URL.revokeObjectURL(url);
        } catch (error) {
            console.error('Error exporting data:', error);
        } finally {
            setLoading(false);
        }
    };

    return (
        <Box sx={{ p: 2 }}>
            <Typography variant="h4" gutterBottom className='page-title'>
                Search Report
            </Typography>
            <Typography variant="body2" sx={{ mb: 4 }}>
                
            </Typography>
            <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                    <TextField
                        placeholder="Query"
                        className="pink-filter"
                        sx={{ height: '35px' }}
                        variant="outlined"
                        size="small"
                        value={filters.search}
                        onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="end">
                                    <SearchIcon />
                                </InputAdornment>
                            ),
                        }}
                    />
                </Box>
                
                <Button
                    variant="outlined"
                    startIcon={<FileDownloadIcon />}
                    onClick={handleExport}
                >
                    Export
                </Button>
            </Box>

            <Paper sx={{ width: '100%', overflow: 'hidden' }}>
                <TableContainer sx={{ maxHeight: 440 }}>
                    <Table stickyHeader aria-label="sticky table">
                        <TableHead>
                            <TableRow>
                                {columns.map((column) => (
                                    <TableCell
                                        key={column.id}
                                        style={{ minWidth: column.minWidth }}
                                    >
                                        {column.label}
                                    </TableCell>
                                ))}
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {loading ? (
                                <TableRow>
                                    <TableCell colSpan={columns.length} align="center">
                                        <CircularProgress />
                                    </TableCell>
                                </TableRow>
                            ) : data.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={columns.length} align="center">
                                        No records found
                                    </TableCell>
                                </TableRow>
                            ) : (
                                data.map((row) => {
                                    const contentId = row.mostClickedModuleId;
                                    const avgData = avgRatingMap[contentId] || { avgRating: 0, totalRatings: 0 };
                                    
                                    return (
                                        <TableRow hover key={`${row.query}-${contentId || 'nocontent'}`}>
                                        <TableCell>{row.query}</TableCell>
                                        <TableCell>{row.matchPercentage}%</TableCell>

                                        {/* Most clicked module (name) */}
                                        <TableCell>
                                            {row.mostClickedModule || 'No content'}
                                        </TableCell>

                                        {/* Avg Rating cell */}
                                        <TableCell>
                                            {contentId ? (
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
                                                    <Button onClick={() => openRatings(row.mostClickedModuleId, row.mostClickedModule)} > 
                                                        {[1, 2, 3, 4, 5].map(i => {
                                                            const rating = avgData.avgRating || 0;
                                                            if (i <= Math.floor(rating)) {
                                                                return <StarIcon key={i} color="warning" fontSize="small" />;
                                                            } else if (i - rating <= 0.5 && i - rating > 0) {
                                                                return <StarHalfIcon key={i} color="warning" fontSize="small" />;
                                                            } else {
                                                                return <StarBorderIcon key={i} fontSize="small" />;
                                                            }
                                                        })}
                                                    </Button>
                                                </Box>
                                            </Box>
                                            ) : (
                                            <Typography variant="body2" color="text.secondary">-</Typography>
                                            )}
                                        </TableCell>
                                        </TableRow>
                                    );
                                })
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
                <TablePagination
                    rowsPerPageOptions={[10, 25, 100]}
                    component="div"
                    count={total}
                    rowsPerPage={rowsPerPage}
                    page={page}
                    onPageChange={handleChangePage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                />
            </Paper>

            <Dialog open={ratingDialog.open} onClose={() => setRatingDialog({ open: false, data: [], contentId: null, contentName: '' })} fullWidth maxWidth="md">
                <DialogTitle>
                    {ratingDialog.contentName}
                    <IconButton
                        aria-label="close"
                        onClick={() => setRatingDialog({ open: false, data: [], contentId: null, contentName: '' })}
                        sx={{ position: 'absolute', right: 8, top: 8 }}
                    >
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>
                <DialogContent dividers>
                    {ratingDialog.loading ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
                            <CircularProgress />
                        </Box>
                    ) : ratingDialog.data.length === 0 ? (
                        <Typography>No ratings found for this content</Typography>
                    ) : (
                        <List sx={{ width: '100%', mx: 'auto' }}>
                            {ratingDialog.data.map((r, index) => (
                                <Paper
                                key={r.id}
                                elevation={2}
                                sx={{
                                    mb: 2,
                                    borderRadius: 2,
                                    p: 2,
                                    backgroundColor: index % 2 === 0 ? 'background.paper' : 'grey.50',
                                }}
                                >
                                <Stack direction="row" alignItems="flex-start" spacing={2}>
                                    <AccountCircleIcon />
                                    <Box flexGrow={1}>
                                    <Stack
                                        direction="row"
                                        alignItems="center"
                                        justifyContent="space-between"
                                        sx={{ mb: 0.5 }}
                                    >
                                        <Typography variant="subtitle1" fontWeight={600}>
                                        {`${r.first_name || ''} ${r.last_name || ''}`}
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary">
                                        {new Date(r.created_at).toLocaleString()}
                                        </Typography>
                                    </Stack>

                                    {/* Rating stars */}
                                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                                        {[1, 2, 3, 4, 5].map((i) => {
                                        const rating = r.rating || 0;
                                            if (i <= Math.floor(rating)) {
                                                return <StarIcon key={i} color="warning" fontSize="small" />;
                                            } else if (i - rating <= 0.5 && i - rating > 0) {
                                                return <StarHalfIcon key={i} color="warning" fontSize="small" />;
                                            } else {
                                                return <StarBorderIcon key={i} fontSize="small" />;
                                            }
                                        })}
                                        <Typography variant="body2" sx={{ ml: 1, fontWeight: 500 }}>
                                        {r.rating.toFixed(1)}
                                        </Typography>
                                    </Box>

                                    {r.comment && (
                                        <Typography
                                        variant="body2"
                                        color="text.secondary"
                                        sx={{ whiteSpace: 'pre-line' }}
                                        >
                                        {r.comment}
                                        </Typography>
                                    )}
                                    </Box>
                                </Stack>
                                </Paper>
                            ))}
                            </List>
                    )}
                </DialogContent>
            </Dialog>
        </Box>
    );
};

export default SearchInsightsReport;